import type { LocalCard, LocalPlace } from './db.ts';

/**
 * "Ricorda questo posto": the app remembers where a card was used, and the next time it opens
 * there it shows that card straight away.
 *
 * What the web allows shapes all of this. There is no geofencing and no background location, so
 * nothing happens while the app is closed: the check runs when it opens. A position takes a few
 * seconds and indoors is off by tens of metres, so the card list is never held back waiting for
 * it — the card replaces the list when, and only if, a match arrives.
 */

export interface Position {
  lat: number;
  lng: number;
  /** Metres. */
  accuracy: number;
}

/** Apple Wallet uses about 100 m for store cards; a supermarket car park needs no less. */
export const PLACE_RADIUS_M = 120;
/** A fix worse than this says nothing useful about which shop you are in. */
export const MAX_USEFUL_ACCURACY_M = 250;
/** Two saves closer than this are the same place; the newer one replaces the older. */
export const SAME_PLACE_M = 60;

const EARTH_RADIUS_M = 6_371_000;
const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in metres (haversine). */
export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * How far from a saved place still counts as "here". The radius grows with the uncertainty of
 * both fixes, capped so that a poor fix cannot claim a whole neighbourhood.
 */
export function matchRadius(place: Pick<LocalPlace, 'accuracy'>, here: Position): number {
  const slack = Math.min(here.accuracy, 100) + Math.min(place.accuracy ?? 0, 50);
  return PLACE_RADIUS_M + slack;
}

export interface NearbyCard {
  card: LocalCard;
  distance: number;
}

/**
 * The cards remembered near this position, nearest first, each once. Empty when the fix is too
 * poor to say anything.
 */
export function cardsNear(here: Position, cards: LocalCard[], places: LocalPlace[]): NearbyCard[] {
  if (here.accuracy > MAX_USEFUL_ACCURACY_M) return [];
  const byId = new Map(cards.filter((c) => !c.deleted).map((c) => [c.id, c]));
  const best = new Map<string, number>();
  for (const place of places) {
    if (place.deleted || !byId.has(place.cardId)) continue;
    const d = distanceM(here, place);
    if (d > matchRadius(place, here)) continue;
    const prev = best.get(place.cardId);
    if (prev === undefined || d < prev) best.set(place.cardId, d);
  }
  return [...best.entries()]
    .map(([id, distance]) => ({ card: byId.get(id)!, distance }))
    .sort((a, b) => a.distance - b.distance);
}

/** The saved place of this card closest to here, if one is within "here". */
export function placeHere(here: Position, places: LocalPlace[]): LocalPlace | null {
  let found: LocalPlace | null = null;
  let bestD = Infinity;
  for (const place of places) {
    if (place.deleted) continue;
    const d = distanceM(here, place);
    if (d <= Math.max(SAME_PLACE_M, matchRadius(place, here)) && d < bestD) {
      found = place;
      bestD = d;
    }
  }
  return found;
}

export function geolocationSupported(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

export class LocationError extends Error {
  constructor(readonly reason: 'unsupported' | 'denied' | 'unavailable' | 'timeout') {
    super(reason);
    this.name = 'LocationError';
  }
}

/** Italian copy for each way getting a position can fail. */
export const LOCATION_MESSAGES: Record<LocationError['reason'], string> = {
  unsupported: 'Questo dispositivo non sa dire dove ti trovi.',
  denied: 'Klarnus non ha il permesso di usare la posizione. Puoi darlo dalle impostazioni del telefono.',
  unavailable: 'Non riesco a capire dove ti trovi. Riprova all’aperto o con il Wi-Fi acceso.',
  timeout: 'La posizione ci sta mettendo troppo. Riprova tra poco.',
};

/**
 * One position. `precise` asks for GPS, which is what saving a place deserves; the check on
 * launch takes whatever the phone already knows (a recent fix, Wi-Fi), because speed matters more
 * there than the last ten metres.
 */
export function getPosition({ precise = false, timeout = 10_000 } = {}): Promise<Position> {
  if (!geolocationSupported()) return Promise.reject(new LocationError('unsupported'));
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => reject(new LocationError(err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'unavailable')),
      { enableHighAccuracy: precise, timeout, maximumAge: precise ? 15_000 : 60_000 },
    );
  });
}

/** Without asking: 'granted' | 'denied' | 'prompt', or 'unknown' where Permissions API is missing. */
export async function locationPermission(): Promise<PermissionState | 'unknown'> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return status?.state ?? 'unknown';
  } catch {
    return 'unknown';
  }
}
